import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { audits } from '../mock';

export default function Gallery() {
  const [index, setIndex] = useState(0);
  const count = audits.length;

  const go = useCallback((dir) => {
    setIndex((prev) => (prev + dir + count) % count);
  }, [count]);

  // Auto-advance every 6s
  useEffect(() => {
    const t = setInterval(() => setIndex((p) => (p + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  const item = audits[index];

  return (
    <section id="gallery" className="py-20 lg:py-24 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-accent font-semibold uppercase tracking-wider text-sm">Compliance in Action</span>
          <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-slate-900 font-display">
            On-Site Compliance Audits
          </h2>
          <p className="mt-3 text-slate-600">
            A visual journey of Lockene Compliance conducting audits at leading global manufacturers.
          </p>
        </div>

        <div className="relative bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Image */}
            <div className="relative h-[280px] md:h-[440px] overflow-hidden">
              {audits.map((a, i) => (
                <img
                  key={a.location}
                  src={a.image}
                  alt={`${a.location} (${a.place})`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
                  onError={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                />
              ))}
              <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 bg-slate-900/80 text-white text-sm font-medium rounded-full px-3 py-1.5 backdrop-blur">
                <MapPin className="w-4 h-4 text-accent" /> {item.location}
              </span>
            </div>

            {/* Text */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h3 className="text-2xl lg:text-3xl font-extrabold text-slate-900 font-display">
                {item.location} <span className="text-primary">({item.place})</span>
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute left-3 top-[140px] md:top-1/2 md:-translate-y-1/2 w-11 h-11 rounded-full bg-white text-slate-800 shadow-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute right-3 top-[140px] md:top-1/2 md:-translate-y-1/2 w-11 h-11 rounded-full bg-accent text-white shadow-lg flex items-center justify-center hover:bg-accent/90 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {audits.map((a, i) => (
            <button
              key={a.location}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-accent' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
